import { useCallback } from "react"
import { useAiStore } from "@/stores/ai-store"
import { useAiContext } from "./use-ai-context"
import {
  useCreateAiConversation,
  useSendAiMessage,
} from "./use-ai-conversations"

/**
 * Main hook for AI chat interactions.
 * Handles creating conversations and sending messages.
 */
export function useAiChat() {
  const context = useAiContext()
  const {
    activeConversationId,
    setActiveConversation,
    setStreaming,
    resetStreamContent,
    open,
  } = useAiStore()

  const createConversation = useCreateAiConversation()
  const sendMessage = useSendAiMessage()

  const send = useCallback(
    async (content: string) => {
      if (!content.trim()) return

      resetStreamContent()
      setStreaming(true)

      let conversationId = activeConversationId

      // Create a new conversation if none active
      if (!conversationId) {
        const result = await createConversation.mutateAsync({
          context_type: context.context_type,
          context_id: context.context_id,
        })
        conversationId = result.data.id
        setActiveConversation(conversationId)
      }

      // Send the message (triggers AiChatJob on backend)
      await sendMessage.mutateAsync({
        conversationId,
        content,
        currentPage: context.current_page,
        contextLabel: context.context_label,
      })
    },
    [
      activeConversationId,
      context,
      createConversation,
      sendMessage,
      setActiveConversation,
      setStreaming,
      resetStreamContent,
    ]
  )

  const startNewChat = useCallback(() => {
    setActiveConversation(null)
    resetStreamContent()
    open()
  }, [setActiveConversation, resetStreamContent, open])

  return {
    send,
    startNewChat,
    isSending: sendMessage.isPending || createConversation.isPending,
  }
}
